// import createStore from 'zustand';
import { CategoryFragment } from '@gql/_generated/categories.generated';
import { EntriesFragment } from '@gql/_generated/entries.generated';
import { ENTRIES_PER_PAGE } from '@lib/constants';
import client from '@lib/fetch/client';
import { withRetries } from '@lib/fetch/withRetries';
import { withSite } from '@lib/hooks';
import { parseApiError } from '@lib/parse/parseApiError';
import { makeNonNullableArray } from '@liquorice/allsorts-craftcms-nextjs';
import { gql } from 'graphql-request';
import { Category, CategoryTypeId, parseCategories } from '../categories/categories';

import { Entry, EntrySectionId, EntryTypeId, parseEntries } from '../entries';
import {
  getOrderByDefault,
  OrderByQueryString,
  ORDER_BY_FIELD_ENUM,
  ORDER_BY_ORDER_ENUM,
} from './orderBy';
import { EntryIndexQueryVariables, getSdk } from './_generated/getEntryIndexData.generated';
import { TicketIndexQueryVariables } from './_generated/getTicketIndexData.generated';

gql`
  query entryIndex(
    $site: [String] = "default"
    $query: String
    $includeCategories: Boolean = false
    $entryLimit: Int = 12
    $entryOffset: Int = 0
    $idQuery: [QueryArgument]
    $categoryLimit: Int = 99
    $entrySection: [String]
    $entryType: [String]
    $categoryType: [String]
    $entryCategoryIds: [QueryArgument]
    $entryCategoryQuery: [CategoryRelationCriteriaInput]
    $relatedToEntries: [EntryRelationCriteriaInput]
    $entryOrderBy: String
    $categoryOrderBy: String
    $indexDisplay: [QueryArgument] = null
    # Event only queries
    $eventDateStart: [QueryArgument] = null
    $eventDateEnd: [QueryArgument] = null
  ) {
    entries: entries(
      site: $site
      id: $idQuery
      search: $query
      section: $entrySection
      type: $entryType
      relatedTo: $entryCategoryIds
      relatedToCategories: $entryCategoryQuery
      relatedToEntries: $relatedToEntries
      limit: $entryLimit
      offset: $entryOffset
      orderBy: $entryOrderBy
      eventDateStart: $eventDateStart
      eventDateEnd: $eventDateEnd
      indexDisplay: $indexDisplay
    ) {
      ...entryCard
    }

    featuredEntries: entries(
      site: $site
      search: $query
      section: $entrySection
      type: $entryType
      relatedTo: $entryCategoryIds
      relatedToCategories: $entryCategoryQuery
      orderBy: $entryOrderBy
      limit: 4
      indexDisplay: $indexDisplay
    ) {
      ...entryCard
    }

    categories: categories(
      site: $site
      search: $query
      group: $categoryType
      limit: $categoryLimit
      orderBy: $categoryOrderBy
    ) @include(if: $includeCategories) {
      ...category
    }

    entryCount(
      site: $site
      search: $query
      section: $entrySection
      type: $entryType
      relatedTo: $entryCategoryIds
      relatedToCategories: $entryCategoryQuery
      relatedToEntries: $relatedToEntries
      eventDateStart: $eventDateStart
      eventDateEnd: $eventDateEnd
      indexDisplay: $indexDisplay
      id: $idQuery
    )
  }
`;

export interface PaginationQuery {
  /** The current page for paginated results */
  page?: number;
  /** The number of results per page */
  perPage?: number;
}

export type EntryIndexQueryProps<
  E extends EntrySectionId,
  T extends EntryTypeId,
  C extends CategoryTypeId
> = PaginationQuery & {
  query?: string;
  /** The "section" of the entries to include */
  entrySection?: E | E[];
  /** The "type" of the entries to include */
  entryType?: T | T[];
  /** The "groups" of the categories to include */
  categoryType?: C | C[];
  /** The "ids" of categories to which entries should be related */
  categoryIds?: ID[];
  /** The "ids" of categories to which entries should be related, grouped by category handle */
  categorySelection?: Record<string, ID[]>;
  /** Query for category results */
  includeCategories?: boolean;
  /** The order of results */
  orderBy?: OrderByQueryString;
  /** Where the entry should be displayed */
  indexDisplay?: string[];
};

export type EntryIndexQueryPropsState<
  E extends EntrySectionId,
  T extends EntryTypeId,
  C extends CategoryTypeId
> = EntryIndexQueryProps<E, T, C> & {
  entrySection?: E[];
  /** The "groups" of the categories to include */
  categoryType?: C[];
};

export type EntryIndexQueryResult<
  E extends EntrySectionId,
  T extends EntryTypeId,
  C extends CategoryTypeId
> = {
  args: EntryIndexQueryProps<E, T, C>;
  customVariables?: EntryIndexQueryVariables;
  entries: Entry<E>[];
  featuredEntries: Maybe<Entry<E>>[];
  categories: { [P in C]?: Category<C>[] };
  entryCount: number;
  /** The current page */
  page: number;
  /** Total number of pages available */
  pages: number;
};

export const parseEntryIndexArgs = <
  E extends EntrySectionId,
  T extends EntryTypeId,
  C extends CategoryTypeId
>(
  maybeArgs: Partial<EntryIndexQueryProps<E, T, C>>
) => {
  const page = Math.max(maybeArgs.page ?? 1, 1);
  const entryLimit = Math.abs(maybeArgs.perPage ?? ENTRIES_PER_PAGE);
  // const excludeIds = maybeArgs.
  const entrySection: E[] = makeNonNullableArray(maybeArgs.entrySection);
  const categoryType: C[] = makeNonNullableArray(maybeArgs.categoryType);

  const args: EntryIndexQueryPropsState<E, T, C> = {
    includeCategories: true,
    orderBy: getOrderByDefault(),
    ...maybeArgs,
    categoryType,
    entrySection,
    perPage: entryLimit,
    page,
  };

  return args;
};

export type PreparedEntryIndexArgs<
  E extends EntrySectionId,
  T extends EntryTypeId,
  C extends CategoryTypeId
> = {
  args: EntryIndexQueryPropsState<E, T, C>;
  variables: EntryIndexQueryVariables;
};

export const prepareEntryIndexArgs = <
  E extends EntrySectionId,
  T extends EntryTypeId,
  C extends CategoryTypeId
>(
  maybeArgs: Partial<EntryIndexQueryProps<E, T, C>>,
  customVariables?: EntryIndexQueryVariables & TicketIndexQueryVariables
): PreparedEntryIndexArgs<E, T, C> => {
  const args = parseEntryIndexArgs(maybeArgs);

  const {
    entrySection,
    entryType,
    categoryType,
    query,
    orderBy,
    categoryIds,
    includeCategories,
    page: maybePage,
    perPage: entryLimit,
    indexDisplay,
  } = args;

  // console.log({ includeCategories });

  // ----------------------------------------------------------------------------------------------
  // ---- Calculate pagination ----
  const page = maybePage || 1;
  const entryOffset = page > 1 ? (page - 1) * (entryLimit ?? ENTRIES_PER_PAGE) : 0;

  // ----------------------------------------------------------------------------------------------
  // ---- Build the query ----
  // const {} = args;

  const isSearch = !!query;

  const variables: EntryIndexQueryVariables = {
    entryLimit,
    entryOffset,
    entrySection,
    entryType,
    categoryType,
    entryCategoryIds: categoryIds ? categoryIds : undefined,
    includeCategories: includeCategories || !categoryType,
    entryOrderBy: orderBy,
    indexDisplay,
    // categoryOrderBy: orderBy,
    ...customVariables,
  };

  // console.log(variables);

  if (isSearch) {
    const searchOrderBy = `${ORDER_BY_FIELD_ENUM.SEARCH_SCORE} ${ORDER_BY_ORDER_ENUM.DESC}`;
    variables.query = query;
    variables.entryOrderBy = orderBy ?? searchOrderBy;
    variables.categoryOrderBy = orderBy ?? searchOrderBy;
  }

  return { args, variables };
};

export const getEntryIndexData = async <
  E extends EntrySectionId,
  T extends EntryTypeId,
  C extends CategoryTypeId
>(
  maybeArgs: EntryIndexQueryProps<E, T, C>,
  customVariables?: EntryIndexQueryVariables
) => {
  const { args, variables } = prepareEntryIndexArgs(maybeArgs, customVariables);

  const sdk = getSdk(client);

  const response = await withRetries(() => sdk.entryIndex(withSite(variables))).catch((err) => {
    console.warn(parseApiError(err));
  });

  const {
    entries: maybeEntries,
    categories: maybeCategories,
    featuredEntries: maybeFeaturedEntries,
    entryCount = 0,
  } = response ?? {};

  // ----------------------------------------------------------------------------------------------
  // ---- Parse the entries ----

  /*
  TODO: the following functions can be restricted by '__typename', but in reality we
  shouldn't have any incorrect types by this point - so we're just using a type assertion.
  */
  const entries = parseEntries(
    maybeEntries as EntriesFragment[] /* , entrySection */
  ) as Entry<E>[];
  const featuredEntries = parseEntries(
    maybeFeaturedEntries as EntriesFragment[] /* , entrySection */
  ) as Maybe<Entry<E>>[];

  // ----------------------------------------------------------------------------------------------
  // ---- Split the categories and parse ----

  const categories = (args.categoryType ?? []).reduce((results, catId) => {
    return {
      ...results,
      [catId]: parseCategories(maybeCategories as CategoryFragment[], catId),
    };
  }, {} as { [P in C]: Category<C>[] });

  const result: EntryIndexQueryResult<E, T, C> = {
    args,
    entries,
    featuredEntries,
    categories,
    entryCount,
    page: args.page ?? 1,
    pages: variables.entryLimit ? Math.ceil(entryCount / variables.entryLimit) : 1,
  };

  return result;
};
